// Iter 74 — Vendor Purchase Orders cross-vendor list.
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    MagnifyingGlass, FileText, CaretLeft, CaretRight, ArrowRight,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
    PO_STATUS_LABEL, PO_STATUS_BADGE, PO_DEFAULT_ENUMS,
    QUOTATION_DEFAULT_ENUMS, formatAmount,
} from "./vendor_constants";

const PAGE_SIZE = 25;

export default function VendorPurchaseOrdersList() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();

    const q = params.get("q") || "";
    const vendorId = params.get("vendor_id") || "";
    const statusFilter = params.get("status") || "all";
    const currency = params.get("currency") || "";
    const sort = params.get("sort") || "recently_added";
    const page = Math.max(1, parseInt(params.get("page") || "1", 10));

    const [searchInput, setSearchInput] = useState(q);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [vendors, setVendors] = useState([]);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (!value) next.delete(key); else next.set(key, value);
        if (key !== "page") next.delete("page");
        setParams(next, { replace: false });
    };

    useEffect(() => {
        let cancelled = false;
        api.get("/vendor-purchase-orders", {
            params: {
                q: q || undefined,
                vendor_id: vendorId || undefined,
                status_filter: statusFilter,
                currency: currency || undefined,
                sort,
                page,
                page_size: PAGE_SIZE,
            },
        })
            .then(({ data }) => {
                if (cancelled) return;
                setRows(data.items || []);
                setTotal(data.total || 0);
                setPages(data.pages || 0);
            })
            .catch((err) => { if (!cancelled) toast.error(formatApiError(err, "Failed to load POs")); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [q, vendorId, statusFilter, currency, sort, page]);

    useEffect(() => {
        let cancelled = false;
        api.get("/vendors", { params: { page_size: 200, sort: "name_asc" } })
            .then(({ data }) => { if (!cancelled) setVendors(data.items || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const t = setTimeout(() => { if (searchInput !== q) updateParam("q", searchInput); }, 300);
        return () => clearTimeout(t);
    }, [searchInput, q]);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="vpo-list-page">
            <PageHeader
                eyebrow="Vendor Management"
                title="Vendor Purchase Orders"
                description="Cross-vendor view of POs derived from accepted vendor quotations. Status auto-shifts to partially_billed/billed as bills are recorded."
            />

            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="vpo-filter-row">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        data-testid="vpo-search"
                        placeholder="Search code, vendor, quote ref…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <select data-testid="vpo-filter-vendor" value={vendorId}
                    onChange={(e) => updateParam("vendor_id", e.target.value)}
                    className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white">
                    <option value="">All vendors</option>
                    {vendors.map((v) => (<option key={v.id} value={v.id}>{v.code} · {v.name}</option>))}
                </select>
                <select data-testid="vpo-filter-status" value={statusFilter}
                    onChange={(e) => updateParam("status", e.target.value)}
                    className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white">
                    <option value="all">All statuses</option>
                    {PO_DEFAULT_ENUMS.statuses.map((s) => (
                        <option key={s} value={s}>{PO_STATUS_LABEL[s]}</option>
                    ))}
                </select>
                <select data-testid="vpo-filter-currency" value={currency}
                    onChange={(e) => updateParam("currency", e.target.value)}
                    className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white">
                    <option value="">All currencies</option>
                    {QUOTATION_DEFAULT_ENUMS.currencies.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
                <select data-testid="vpo-sort" value={sort}
                    onChange={(e) => updateParam("sort", e.target.value)}
                    className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white ml-auto">
                    <option value="recently_added">Recently added</option>
                    <option value="recently_updated">Recently updated</option>
                    <option value="po_date_desc">PO date ↓</option>
                    <option value="po_date_asc">PO date ↑</option>
                    <option value="grand_total_desc">Grand total ↓</option>
                    <option value="grand_total_asc">Grand total ↑</option>
                    <option value="billing_progress_desc">Billed % ↓</option>
                </select>
            </div>

            <div className="mt-5 rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="vpo-table">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="text-left font-medium px-4 py-3">Code</th>
                                <th className="text-left font-medium px-4 py-3">Vendor</th>
                                <th className="text-left font-medium px-4 py-3">From Quote</th>
                                <th className="text-left font-medium px-4 py-3">PO Date</th>
                                <th className="text-right font-medium px-4 py-3">Grand Total</th>
                                <th className="text-right font-medium px-4 py-3">Billed</th>
                                <th className="text-right font-medium px-4 py-3">Progress</th>
                                <th className="text-left font-medium px-4 py-3">Status</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={9} className="text-center text-slate-400 py-12">Loading…</td></tr>}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={9} className="py-16">
                                    <EmptyState icon={<FileText size={32} className="text-slate-400" />}
                                        title="No purchase orders" message="Convert an accepted vendor quotation to create a PO." />
                                </td></tr>
                            )}
                            {!loading && rows.map((p) => {
                                const badge = PO_STATUS_BADGE[p.status] || PO_STATUS_BADGE.draft;
                                return (
                                    <tr key={p.id} data-testid={`vpo-row-${p.code}`}
                                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                        onClick={() => navigate(`/vendor-purchase-orders/${p.id}`)}>
                                        <td className="px-4 py-3 font-mono text-xs">{p.code}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-slate-900">{p.vendor_name}</div>
                                            <div className="text-xs text-slate-500 font-mono">{p.vendor_code}</div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-slate-700 font-mono">{p.vendor_quotation_code}</td>
                                        <td className="px-4 py-3 text-xs text-slate-700">{formatDate(p.po_date)}</td>
                                        <td className="px-4 py-3 text-right font-mono text-slate-900">{formatAmount(p.grand_total, p.currency)}</td>
                                        <td className="px-4 py-3 text-right font-mono text-xs text-slate-700">{formatAmount(p.billed_amount, p.currency)}</td>
                                        <td className="px-4 py-3 text-right text-xs">
                                            <div className="inline-flex items-center gap-2">
                                                <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                                    <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, p.billing_progress_pct || 0)}%` }} />
                                                </div>
                                                <span className="text-slate-600 font-mono">{Number(p.billing_progress_pct || 0).toFixed(0)}%</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <Badge data-testid={`vpo-status-${p.code}`} className={`${badge.className} hover:${badge.className}`}>
                                                {badge.label}
                                            </Badge>
                                        </td>
                                        <td className="px-4 py-3 text-right"><ArrowRight size={14} className="text-slate-400" /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {!loading && total > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-xs text-slate-600">
                        <div data-testid="vpo-pagination-summary">
                            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button data-testid="vpo-page-prev" size="sm" variant="outline"
                                disabled={page <= 1} onClick={() => updateParam("page", page - 1)}><CaretLeft size={14} /></Button>
                            <span className="px-2">Page {page} of {pages}</span>
                            <Button data-testid="vpo-page-next" size="sm" variant="outline"
                                disabled={page >= pages} onClick={() => updateParam("page", page + 1)}><CaretRight size={14} /></Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
